<template>
  <div class="cb-shell">
    <div class="cb-topbar">
      <span class="cb-badge">
        <span class="cb-badge-dot"></span>{{ $t('complete') }}
      </span>
      <button type="button" class="cb-close" aria-label="Close" @click.prevent="$emit('close')">
        <i class="fa fa-times"></i>
      </button>
    </div>

    <div class="cb-header">
      <div class="cb-success-icon">
        <i class="fa fa-check"></i>
      </div>
      <h1 class="cb-title">{{ $t('customerMemberCard.checkBillTitle') }}</h1>
      <p class="cb-subtitle">{{ $t('customerMemberCard.actionCompleted') }}</p>
    </div>

    <div class="cb-body">
      <div class="cb-cashback">
        <div class="cb-cashback-left">
          <div class="cb-cashback-icon">
            <i class="fa fa-coins"></i>
          </div>
          <div>
            <p class="cb-cashback-label">{{ $t('customerMemberCard.cashBackChange') }}</p>
            <p class="cb-cashback-value">USD {{ formatNumber(data.changeAmount) }}</p>
          </div>
        </div>
        <span class="cb-cashback-tag">
          {{ data.changeAmount > 0 ? $t('customerMemberCard.changeGiven') : $t('customerMemberCard.exactAmountPaid') }}
        </span>
      </div>

      <div class="cb-summary">
        <div class="cb-row">
          <span class="cb-row-label">{{ $t('customerMemberCard.customer') }}</span>
          <span class="cb-row-value">
            {{ data.customerName || '-' }}
            <span class="cb-row-muted" v-if="data.contactNumber">({{ data.contactNumber }})</span>
          </span>
        </div>
        <div class="cb-row">
          <span class="cb-row-label">{{ $t('customerMemberCard.serialCard') }}</span>
          <span class="cb-row-value cb-row-inline">
            <span class="cb-mono">{{ data.serialCard }}</span>
            <span class="cb-tag" v-if="data.cardTypeLabel">{{ data.cardTypeLabel }}</span>
          </span>
        </div>
        <div class="cb-row">
          <span class="cb-row-label">{{ $t('customerMemberCard.paymentMode') }}</span>
          <span class="cb-row-value cb-row-inline">
            <i :class="paymentIcon"></i>
            <span>{{ paymentModeLabel }}</span>
          </span>
        </div>

        <div class="cb-divider"></div>

        <div class="cb-total-row">
          <div>
            <p class="cb-total-label">{{ $t('customerMemberCard.grandTotal') }}</p>
            <p class="cb-total-sub">{{ $t('customerMemberCard.totalSettledAmount') }}</p>
          </div>
          <div class="cb-total-value">
            <span class="cb-mono cb-total-amount">$ {{ formatNumber(data.grandTotal) }}</span>
            <span class="cb-total-currency">USD</span>
          </div>
        </div>
      </div>
    </div>

    <footer class="cb-footer">
      <button type="button" class="cb-btn-primary" @click.prevent="$emit('close')">
        {{ $t('ok') }}
        <i class="fa fa-check"></i>
      </button>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'checkBillSuccess',
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  computed: {
    paymentIcon() {
      return this.data.paymentMethodId === 3 ? 'fa fa-university' : 'fa fa-money-bill-wave'
    },
    paymentModeLabel() {
      if (this.data.paymentMethodId === 3) {
        return this.data.bankName || this.$t('martSale.bankTransfer')
      }
      return this.$t('restaurantSale.cashPayment')
    }
  },
  methods: {
    formatNumber(value) {
      return parseFloat(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;600;700&display=swap');

.cb-shell {
  display: flex;
  flex-direction: column;
  font-family: 'Manrope', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.cb-mono {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
}

.cb-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 0;
}

.cb-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.cb-badge-dot {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: #10b981;
}

.cb-close {
  border: none;
  background: transparent;
  color: #94a3b8;
  padding: 6px;
  border-radius: 9999px;
  cursor: pointer;
  line-height: 1;
  outline: none;
}

.cb-close:hover {
  color: #334155;
  background: #f1f5f9;
}

.cb-header {
  padding: 12px 24px 20px;
  text-align: center;
}

.cb-success-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 12px;
  border-radius: 16px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #059669;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  box-shadow: 0 0 20px -3px rgba(16, 185, 129, 0.35);
}

.cb-title {
  font-size: 19px;
  font-weight: 800;
  color: #0f172a;
  text-transform: uppercase;
  letter-spacing: -0.01em;
  margin: 0;
}

.cb-subtitle {
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  margin: 2px 0 0;
}

.cb-body {
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.cb-cashback {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 12px;
  background: linear-gradient(to right, rgba(224, 242, 254, 0.5), rgba(219, 234, 254, 0.5));
  border: 1px solid #bae6fd;
}

.cb-cashback-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cb-cashback-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #e0f2fe;
  color: #0284c7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
}

.cb-cashback-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #64748b;
  margin: 0;
}

.cb-cashback-value {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.cb-cashback-tag {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.85);
  color: #0369a1;
  border: 1px solid #bae6fd;
  white-space: nowrap;
}

.cb-summary {
  background: rgba(248, 250, 252, 0.8);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cb-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  gap: 8px;
}

.cb-row-label {
  color: #64748b;
  font-weight: 500;
  flex-shrink: 0;
}

.cb-row-value {
  color: #1e293b;
  font-weight: 600;
  text-align: right;
}

.cb-row-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.cb-row-muted {
  color: #94a3b8;
  font-weight: 400;
}

.cb-tag {
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  background: #dbeafe;
  color: #1d63d8;
}

.cb-divider {
  border-top: 1px dashed #e2e8f0;
  margin: 2px 0;
}

.cb-total-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.cb-total-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  margin: 0;
}

.cb-total-sub {
  font-size: 11px;
  color: #94a3b8;
  margin: 0;
}

.cb-total-value {
  text-align: right;
}

.cb-total-amount {
  font-size: 20px;
  font-weight: 800;
  color: #257afa;
  letter-spacing: -0.01em;
}

.cb-total-currency {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  margin-left: 2px;
}

.cb-footer {
  padding: 20px;
  margin-top: 6px;
  background: rgba(248, 250, 252, 0.5);
  border-top: 1px solid #f1f5f9;
}

.cb-btn-primary {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border: none;
  border-radius: 12px;
  background: #257afa;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  transition: all 0.15s ease;
}

.cb-btn-primary:hover {
  background: #1d63d8;
}

.cb-btn-primary:focus-visible {
  box-shadow: 0 0 0 3px rgba(37, 122, 250, 0.3);
}
</style>
